<template>
  <MembershipDialog
    :value="visible"
    dialog-type="ama"
    :form="dialogForm"
    :buttonColor="buttonColor"
    :numberAvailable="numberAvailable"
    :errorAmaMsg="errorAmaMsg"
    :showAmaInfo="false"
    :isAccountManager="false"
    :membershipItems="[]"
    :loading="busy"
    @input="onVisibleChange"
    @validate="onValidate"
    @add="onAdd"
    @cancel="onCancel"
    @update:memberId="onMemberIdUpdate"
  />
</template>

<script>
import axios from "axios";
import MembershipService from "@/services/MembershipService";
import MembershipDialog from "@/components/yourMembership/MembershipDialog";

export default {
  name: "AddAmaDialog",
  components: { MembershipDialog },
  props: {
    value: { type: Boolean, default: false },
    participant: { type: Object, default: null },
    // When opened for a participant who already has an AMA number on
    // file, the parent passes it here so the dialog shows it pre-filled
    // and the user can correct / renew / replace it. Null for legacy
    // "Add AMA" flow with no existing number.
    initialAmaNumber: { type: String, default: null },
    validationDate: { type: String, default: null },
    buttonColor: { type: String, default: "primary" }
  },
  data() {
    return {
      busy: false,
      errorAmaMsg: "",
      numberAvailable: false,
      verifiedOrganizationId: null,
      verifiedOrganizerId: null,
      dialogForm: {
        formName: "AMA",
        organizationName: "AMA",
        organizationId: null,
        memberId: null,
        expirationDate: null,
        lifemembercheckbox: false,
        userId: null
      }
    };
  },
  computed: {
    visible() { return this.value; },
    participantName() {
      if (!this.participant) return "";
      return `${this.participant.fName || this.participant.firstName || ""} ${this.participant.lName || this.participant.lastName || ""}`.trim();
    },
    participantDob() {
      if (!this.participant) return "";
      return this.participant.birthday || this.participant.dateOfBirth || "";
    }
  },
  watch: {
    value(v) {
      if (v) this.reset();
    },
    participant() {
      if (this.value) this.reset();
    },
    initialAmaNumber() {
      // Reset on prop change so switching between participants (each
      // with their own existing AMA number) picks up the right pre-fill.
      if (this.value) this.reset();
    }
  },
  methods: {
    reset() {
      this.busy = false;
      this.errorAmaMsg = "";
      this.numberAvailable = false;
      this.verifiedOrganizationId = null;
      this.verifiedOrganizerId = null;
      this.dialogForm = {
        formName: "AMA",
        organizationName: "AMA",
        organizationId: null,
        // Pre-fill with the existing AMA number (Manage AMA flow) so the
        // user sees what's on file. Stays null when opening for a
        // participant with no AMA yet.
        memberId: this.initialAmaNumber || null,
        expirationDate: null,
        lifemembercheckbox: false,
        userId: this.participant ? (this.participant.userId || this.participant.id) : null
      };
    },
    onVisibleChange(v) {
      this.$emit("input", v);
    },
    onMemberIdUpdate(value) {
      this.dialogForm = { ...this.dialogForm, memberId: value };
      if (this.numberAvailable) {
        // Input changed after a successful validation — require re-validate.
        this.numberAvailable = false;
        this.dialogForm.expirationDate = null;
        this.dialogForm.lifemembercheckbox = false;
      }
      if (this.errorAmaMsg) this.errorAmaMsg = "";
    },
    async resolveAmaOrg() {
      try {
        const url = `${this.$store.getters.env.get('VUE_APP_API_LINK')}/participant/membership/ama-id`;
        const resp = await axios.get(url);
        if (resp && resp.data && resp.data.success === 1 && resp.data.message) return resp.data.message;
      } catch (e) { /* fall through */ }
      return null;
    },
    isParticipantOwner() {
      const id = Number(this.participant?.userId || this.participant?.id || 0);
      const currentUserId = Number(this.$store?.state?.session?.user?.id || 0);
      return id && id === currentUserId;
    },
    isParticipantAccountManager() {
      const id = Number(this.participant?.userId || this.participant?.id || 0);
      const currentUserId = Number(this.$store?.state?.session?.user?.id || 0);
      if (!id) return false;
      const managed = Array.isArray(this.$store?.state?.session?.participants) ? this.$store.state.session.participants.slice(1) : [];
      return id === currentUserId || managed.some((p) => Number(p?.userId || p?.id) === id);
    },
    amaValidationDate() {
      return this.validationDate || new Date().toISOString().slice(0, 10);
    },
    async onValidate() {
      if (!this.participant || !this.dialogForm.memberId) return;
      this.busy = true;
      this.errorAmaMsg = "";
      this.numberAvailable = false;
      try {
        const amaOrg = await this.resolveAmaOrg();
        if (!amaOrg || !amaOrg.id || !amaOrg.organizerId) {
          this.errorAmaMsg = this.$t("memberRegistration.ama_add_dialog.error_ama_org_missing");
          return;
        }
        this.verifiedOrganizationId = amaOrg.id;
        this.verifiedOrganizerId = amaOrg.organizerId;
        this.dialogForm.organizationId = amaOrg.id;

        const payload = {
          amaNumber: this.dialogForm.memberId,
          userId: (this.participant.userId || this.participant.id),
          user: this.participantName,
          birthDate: this.participantDob,
          date: this.amaValidationDate(),
          eventEndDate: this.validationDate || null,
          organizationId: amaOrg.id,
          participantName: this.participantName.replace(/[^a-zA-Z]/g, "")
        };

        const response = await MembershipService.validateAmaNumber(payload);
        const msg = response && response.data && response.data.message;

        if (!response || response.data.success !== 1 || !msg || msg.isFound !== true) {
          this.errorAmaMsg = this.$t("memberRegistration.ama_add_dialog.error_not_found");
          return;
        }
        if (Array.isArray(msg.errors) && msg.errors.length) {
          // Name / DOB mismatch against the AMA record. We don't surface the
          // profile-update flow here (registration flow is not the place to
          // edit a profile); point the user at the dedicated Membership page.
          let actionHintKey = "memberRegistration.ama_add_dialog.mismatch_action_hint";
          if (this.isParticipantAccountManager() && !this.isParticipantOwner()) {
            actionHintKey = "memberRegistration.ama_add_dialog.mismatch_action_managed";
          } else if (!this.isParticipantAccountManager()) {
            actionHintKey = "memberRegistration.ama_add_dialog.mismatch_action_other";
          }
          this.errorAmaMsg = this.$t("memberRegistration.ama_add_dialog.mismatch_title")
            + "<br><br><span style='color: #ff0000; font-weight: bold;'>" + this.$t(actionHintKey) + "</span>";
          return;
        }

        // Success: populate the form so MembershipDialog shows the expiration
        // row and enables the Save button via numberAvailable.
        if (msg.expiration) {
          const expStr = String(msg.expiration);
          if (expStr.toLowerCase().includes("life")) {
            this.dialogForm.expirationDate = "9999-12-31";
            this.dialogForm.lifemembercheckbox = true;
          } else {
            this.dialogForm.expirationDate = expStr;
            this.dialogForm.lifemembercheckbox = false;
          }
        }
        this.numberAvailable = true;
      } catch (e) {
        this.errorAmaMsg = this.$t("memberRegistration.ama_add_dialog.error_generic");
      } finally {
        this.busy = false;
      }
    },
    async onAdd() {
      if (!this.participant || !this.dialogForm.memberId || !this.verifiedOrganizationId) return;
      this.busy = true;
      this.errorAmaMsg = "";
      try {
        // Backend convention (Membership.php::createMembership): payload
        // `organizerId` is looked up as organization.id, so it must be the
        // AMA organization id (25), not a user id.
        const createPayload = {
          organizerId: this.verifiedOrganizationId,
          organizationName: "AMA",
          allowEmails: 1,
          lifemembercheckbox: this.dialogForm.lifemembercheckbox ? 1 : 0,
          expDate: this.dialogForm.expirationDate,
          memberId: this.dialogForm.memberId,
          userId: (this.participant.userId || this.participant.id),
          isAma: true,
          isD14: false
        };
        const response = await MembershipService.create(createPayload);
        const success = response && response.data && response.data.success === 1;
        const msg = response && response.data && response.data.message;

        // Treat duplicate (already on file) as success so parent refreshes
        // and picks up the existing row.
        if (!success && msg !== "error_duplicate") {
          this.errorAmaMsg = this.$t("memberRegistration.ama_add_dialog.error_save_failed");
          return;
        }

        this.$emit("added", {
          participant: this.participant,
          amaNumber: this.dialogForm.memberId,
          expiration: this.dialogForm.expirationDate
        });
        this.$emit("input", false);
      } catch (e) {
        this.errorAmaMsg = this.$t("memberRegistration.ama_add_dialog.error_generic");
      } finally {
        this.busy = false;
      }
    },
    onCancel() {
      this.$emit("input", false);
      this.$emit("cancelled", { participant: this.participant });
    }
  }
};
</script>
